/**
 * In-memory ring buffer of recent API calls, for the staging debug panel.
 *
 * Mobile browsers have no network tab, and Safari's Web Inspector needs a Mac
 * on the other end of a cable. This keeps the last N requests in memory so a
 * phone can see what the app actually sent and got back.
 *
 * Staging only -- see isDebugEnabled(). Never records in production: request
 * and response bodies routinely contain tokens and user data, and this buffer
 * is readable by anything running on the page.
 */
const MAX_ENTRIES = 50;

const entries = [];
const listeners = new Set();

// Written as a direct comparison rather than through optional chaining: Vite
// substitutes import.meta.env.VITE_ENVIRONMENT with a string literal at build
// time, so this folds to `false` in production and the minifier drops the panel
// and this buffer entirely. Anything less literal ships the code to production
// inert, which means a request log in a bundle that has no business holding one.
export const DEBUG_ENABLED = import.meta.env.VITE_ENVIRONMENT === 'staging';

export function isDebugEnabled() {
  return DEBUG_ENABLED;
}

function notify() {
  listeners.forEach((fn) => {
    try { fn(entries); } catch { /* a broken listener must not break logging */ }
  });
}

export function recordRequest(entry) {
  if (!DEBUG_ENABLED) return;
  entries.unshift({ ...entry, id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}` });
  if (entries.length > MAX_ENTRIES) entries.length = MAX_ENTRIES;
  notify();
}

export function getRequests() {
  return entries;
}

export function clearRequests() {
  entries.length = 0;
  notify();
}

export function subscribe(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}
